import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyMedia } from "@/components/ui/empty";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { exportDetectionsToCSV, DetectionsQueryParams, useDetectionsQuery } from "@/api/v0/detections";
import { useSensorsQueryV1 } from "@/api/v1/sensor-api";
import { useGeofencesQueryV1 } from "@/api/v1/geofence-api";
import { useFlightRestrictionZonesQueryV1 } from "@/api/v1/flight-restriction-zone-api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { ProOnlyGuard } from "@/components/pro-only-guard";
import {
  BarChart3,
  Download, TrendingUp,
  Activity,
  Plane,
  Antenna,
  Shield,
  Fence,
  Clock,
  AlertTriangle,
  CheckCircle, LineChart
} from "lucide-react";
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart as RechartsLineChart, Line } from "recharts";

export const AnalyticsRoute = () => {
  const [filters, setFilters] = useState<DetectionsQueryParams>({
    limit: 10000, // Maximum allowed by API
  });
  const [isExporting, setIsExporting] = useState(false);
  const { data: sensors, isLoading: sensorsLoading } = useSensorsQueryV1();
  const { data: geofences, isLoading: geofencesLoading } = useGeofencesQueryV1();
  const { data: flightRestrictionZones, isLoading: zonesLoading } = useFlightRestrictionZonesQueryV1();
  
  // Query detections for analytics
  const queryParams = useMemo(() => {
    const last7Days = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return {
      startDate: last7Days.toISOString(),
      limit: 1000, // Reasonable limit for analytics
    };
  }, []);

  const { data: detectionsResponse, isLoading: detectionsLoading } = useDetectionsQuery(queryParams);

  const isLoading = detectionsLoading || sensorsLoading || zonesLoading || geofencesLoading;

  const handleExport = async () => {
    try {
      setIsExporting(true);

      if (filters.sensor_id === "all") {
        delete filters.sensor_id;
      }

      const blob = await exportDetectionsToCSV(filters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `detections-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Export completed successfully");
    } catch  {
      toast.error("Failed to export detections");
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate analytics data
  const analyticsData = useMemo(() => {
    if (!detectionsResponse?.detections) return null;

    const detections = detectionsResponse.detections;
    const totalDetections = detections.length;
    const uniqueDrones = new Set(detections.map(d => d.drone_identifier)).size;
    const activeSensors = new Set(detections.map(d => d.sensor_id)).size;

    // Group by hour for activity chart
    const hourlyActivity = Array.from({ length: 24 }, (_, hour) => {
      const hourDetections = detections.filter(d => {
        const detectionHour = new Date(d.detection_time).getHours();
        return detectionHour === hour;
      });
      return {
        hour: `${hour.toString().padStart(2, '0')}:00`,
        detections: hourDetections.length,
        drones: new Set(hourDetections.map(d => d.drone_identifier)).size
      };
    });

    // Group by sensor
    const sensorData = detections.reduce((acc, detection) => {
      const sensorId = detection.sensor_id;
      const sensorName = sensors?.find(s => s.slug === sensorId || s.organisation_id.toString() === sensorId)?.name || sensorId;
      
      if (!acc[sensorName]) {
        acc[sensorName] = { detections: 0, drones: new Set() };
      }
      acc[sensorName].detections++;
      acc[sensorName].drones.add(detection.drone_identifier);
      
      return acc;
    }, {} as Record<string, { detections: number; drones: Set<string> }>);

    const sensorChartData = Object.entries(sensorData).map(([sensor, data]) => ({
      sensor,
      detections: data.detections,
      uniqueDrones: data.drones.size
    }));

    return {
      totalDetections,
      uniqueDrones,
      activeSensors,
      hourlyActivity,
      sensorChartData
    };
  }, [detectionsResponse, sensors]);

  const chartConfig = {
    detections: {
      label: "Detections",
    },
    drones: {
      label: "Unique Drones",
    },
    hour: {
      label: "Hour",
    },
    sensor: {
      label: "Sensor",
    },
  };

  return (
    <ProOnlyGuard
      title="Analytics Dashboard"
      description="Access comprehensive analytics and insights with a Pro subscription."
    >
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Analytics", href: "/analytics" },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">Analytics</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Comprehensive insights into drone activity and system performance
            </p>
          </div>
          <Badge variant="outline" className="w-fit">
            <BarChart3 className="h-3 w-3 mr-1" />
            Data Insights
          </Badge>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
          {/* Total Detections */}
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Total Detections</CardTitle>
              <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              {isLoading ? (
                <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
              ) : (
                <div className="text-lg sm:text-2xl font-bold">{analyticsData?.totalDetections ?? 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : "Last 7 days"}
              </p>
            </CardContent>
          </Card>

          {/* Unique Drones */}
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Unique Drones</CardTitle>
              <Plane className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              {isLoading ? (
                <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
              ) : (
                <div className="text-lg sm:text-2xl font-bold">{analyticsData?.uniqueDrones ?? 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : "Different aircraft"}
              </p>
            </CardContent>
          </Card>

          {/* Active Sensors */}
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Active Sensors</CardTitle>
              <Antenna className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              {isLoading ? (
                <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
              ) : (
                <div className="text-lg sm:text-2xl font-bold">{analyticsData?.activeSensors ?? 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : "Detecting activity"}
              </p>
            </CardContent>
          </Card>

          {/* System Health */}
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">System Health</CardTitle>
              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold text-green-600">Good</div>
              <p className="text-xs text-muted-foreground">
                All systems operational
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-4 h-8 sm:h-10">
            <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
            <TabsTrigger value="activity" className="text-xs sm:text-sm">Activity</TabsTrigger>
            <TabsTrigger value="sensors" className="text-xs sm:text-sm">Sensors</TabsTrigger>
            <TabsTrigger value="export" className="text-xs sm:text-sm">Export</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 sm:space-y-6">
            <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
              {/* Hourly Activity Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChart className="h-5 w-5" />
                    Hourly Activity
                  </CardTitle>
                  <CardDescription>Detection patterns throughout the day</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="space-y-4">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ) : analyticsData?.hourlyActivity ? (
                    <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[300px]">
                      <RechartsLineChart data={analyticsData.hourlyActivity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line 
                          type="monotone" 
                          dataKey="detections" 
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          name="Detections"
                        />
                      </RechartsLineChart>
                    </ChartContainer>
                  ) : (
                    <Empty>
                      <EmptyMedia variant="icon">
                        <Activity className="h-6 w-6" />
                      </EmptyMedia>
                      <EmptyHeader>
                        <EmptyTitle>No activity data</EmptyTitle>
                        <EmptyDescription>Detection activity will appear here once data is available.</EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  )}
                </CardContent>
              </Card>

              {/* System Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    System Overview
                  </CardTitle>
                  <CardDescription>Current system status and coverage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Flight Restriction Zones</span>
                      <Badge variant="default" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-green-200">
                        {flightRestrictionZones?.filter(z => {
                          const now = new Date();
                          const beginTime = new Date(z.effective_begin_time);
                          const endTime = z.effective_end_time ? new Date(z.effective_end_time) : null;
                          return beginTime <= now && (!endTime || endTime >= now);
                        }).length ?? 0} Active
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Geofences</span>
                      <Badge variant="outline">
                        <Fence className="h-3 w-3 mr-1" />
                        {geofences?.length ?? 0} Total
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Data Freshness</span>
                      <Badge variant="outline">
                        <Clock className="h-3 w-3 mr-1" />
                        Real-time
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coverage Area</span>
                      <Badge variant="outline">
                        <Shield className="h-3 w-3 mr-1" />
                        United Kingdom
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Detection Activity
                </CardTitle>
                <CardDescription>Detailed breakdown of drone detection activity</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center space-x-4">
                        <Skeleton className="h-12 w-12 rounded" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-[200px]" />
                          <Skeleton className="h-4 w-[100px]" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : detectionsResponse?.detections?.length ? (
                  <div className="space-y-4">
                    {detectionsResponse.detections.slice(0, 10).map((detection) => (
                      <div key={detection.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900">
                            <Plane className="h-4 w-4 text-orange-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold">{detection.drone_identifier}</h4>
                            <p className="text-sm text-muted-foreground">
                              Detected by {sensors?.find(s => s.slug === detection.sensor_id || s.organisation_id.toString() === detection.sensor_id)?.name ?? detection.sensor_id}
                            </p>
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(detection.detection_time).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Empty>
                    <EmptyMedia variant="icon">
                      <Activity className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyHeader>
                      <EmptyTitle>No detection activity</EmptyTitle>
                      <EmptyDescription>Drone detections will appear here once sensors start detecting activity.</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sensors" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Antenna className="h-5 w-5" />
                  Sensor Performance
                </CardTitle>
                <CardDescription>Detection performance by sensor</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : analyticsData?.sensorChartData?.length ? (
                  <div className="space-y-4">
                    <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[300px]">
                      <RechartsBarChart data={analyticsData.sensorChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="sensor" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar dataKey="detections" fill="#3b82f6" name="Detections" />
                      </RechartsBarChart>
                    </ChartContainer>
                    <div className="space-y-2">
                      {analyticsData.sensorChartData.map((sensor) => (
                        <div key={sensor.sensor} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Antenna className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">{sensor.sensor}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold">{sensor.detections} detections</div>
                            <div className="text-xs text-muted-foreground">{sensor.uniqueDrones} unique drones</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Empty>
                    <EmptyMedia variant="icon">
                      <Antenna className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyHeader>
                      <EmptyTitle>No sensor data</EmptyTitle>
                      <EmptyDescription>Sensor performance data will appear here once sensors start detecting activity.</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="export" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Export Data
                </CardTitle>
                <CardDescription>Export detection data for external analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="start_time">Start Time</Label>
                      <DatePicker
                        date={filters.start_time ? new Date(filters.start_time) : undefined}
                        setDate={(date) => setFilters(prev => ({
                          ...prev,
                          start_time: date?.toISOString()
                        }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end_time">End Time</Label>
                      <DatePicker
                        date={filters.end_time ? new Date(filters.end_time) : undefined}
                        setDate={(date) => setFilters(prev => ({
                          ...prev,
                          end_time: date?.toISOString()
                        }))}
                      />
                    </div>
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="sensor">Sensor</Label>
                      <Select
                        value={filters.sensor_id || "all"}
                        onValueChange={(value) => setFilters(prev => ({
                          ...prev,
                          sensor_id: value === "all" ? undefined : value
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a sensor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Sensors</SelectItem>
                          {sensors?.map((sensor) => (
                            <SelectItem key={sensor.slug || sensor.organisation_id.toString()} value={sensor.slug || sensor.organisation_id.toString()}>
                              {sensor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="drone_identifier">Drone Identifier</Label>
                      <Input
                        id="drone_identifier"
                        value={filters.drone_identifier || ""}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          drone_identifier: e.target.value
                        }))}
                        placeholder="Enter drone identifier"
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2 justify-end">
                    <Button 
                      onClick={handleExport}
                      disabled={isExporting}
                      className="w-full sm:w-auto"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {isExporting ? "Exporting..." : "Export to CSV"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* No Data State */}
        {!isLoading && (!detectionsResponse?.detections?.length && !sensors?.length) && (
          <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">No Analytics Data Available</p>
                <p>
                  Analytics data will appear here once you have sensors set up and detecting drone activity. 
                  <a href="/sensors" className="text-blue-600 hover:underline ml-1">
                    Set up your first sensor
                  </a> to start collecting data for analysis.
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </div>
    </main>
    </ProOnlyGuard>
  );
};
